// Implements: F-CNV-003, F-CNV-007, F-CNV-008, F-CNV-009, F-CNV-013, F-UIX-009, F-UIX-010
// Implements: F-PRV-003
/**
 * One turn of the conversation, from the press of "send" to the refresh of
 * everything it changed.
 *
 * The server ALWAYS answers the POST with `202` and the id the run will be recorded under
 * (D-N12); what became of the turn is read from the conversation state, never from the POST:
 *
 *   1. `start` sends the text (never retried: it is paid) with a turn id the
 *      client chose. The SAME id is kept for a resend of the same text until the
 *      server answers for it, so a resend after a lost response cannot start a
 *      second paid turn: the server refuses the duplicate with a `409`, and the
 *      hook then finds its own turn in the conversation state and follows it.
 *   2. The turn is first `assessing` (the server decides whether to ask): the phase is
 *      `evaluating`, there is no run to stream yet, and the state is read again with a
 *      growing, bounded pause until the turn is `dispatched` or closed. Only a dispatched
 *      turn has a run: its progress is the generation `/stream`, followed by the shared
 *      `followStream` (bounded retry of a `404`, reconnects, budget of consecutive failures).
 *   2a. Every `in_progress` event also says where the run is in its flow (F-PRV-003): its stage,
 *      the steps done and their total. That is `progress`, published only from an event that
 *      names the run being followed: stages of another run are ignored, and a reading behind
 *      the one shown (fewer steps done) never moves the stage backwards. The stages are drawn only while the phase is `running`, which a run reaches
 *      by its own first event, so a new run never shows the stages of the one before.
 *   2b. The OUTCOME is `last_turn.status` of the state, matched by the id of the turn sent
 *      here, read once the turn is no longer open. The run record says `succeeded` before the
 *      turn closes (its summary is frozen first), so the stream's last event never decides
 *      anything and the composer stays locked until the turn is closed. A turn whose end
 *      never shows within the budget ends as `interrupted`; a `last_turn` that is not the
 *      one sent here claims nothing (the thread holds the truth).
 *   3. `cancel` asks the server to stop the turn (`POST .../conversation/cancel`). `202`
 *      is NOT the run having stopped: the phase becomes `cancelling` and only the
 *      closed turn says `cancelled`. There is no run id to wait for, so nothing is retried
 *      on a `404`; a `409` ("nothing is running") is the race of a run that ended first.
 *   4. A send whose answer was lost keeps its id as possibly held by the server, so a `409`
 *      that names it is read as "taken" (the turn is found by id, then in `last_turn`), never
 *      as an error to show forever (10.4).
 *   4b. `retryLast` and `edit` (F-CNV-008, F-CNV-009) are sends too: the same reserved-id
 *      rule, keyed by the intention (the text, the turn retried, the message edited and its
 *      words), the same refusal handling and the same follow of the new turn.
 *   4c. `confirm` (F-CNV-016) draws from the brief of a turn that closed `briefed`: the same
 *      reserved-id rule, keyed by the briefed turn and the text, and the same follow. A turn
 *      that closes `briefed` ends in the phase `briefed`; the container shows the brief.
 *   5. `adopt` follows a turn somebody else opened (another tab, a reload), the
 *      one `GET .../conversation` reports, also after this screen ran its own; and the first
 *      turn the creation of the mockup started (7.6), which nothing here sends again.
 *
 * Leaving the screen, or starting again, abandons what was in flight: a POST
 * that answers after that opens nothing, and a cancel stops retrying.
 *
 * When the turn closes the hook refreshes what it may have changed: the thread (its
 * questions, update or error message), the conversation state, the version tree and the
 * mockup detail.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { abortableDelay as wait } from "../../../shared/api/abortableDelay";
import { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { DEFAULT_STREAM_RETRY, backoffDelay, followStream } from "../../../shared/api/followRun";
import type { StreamRetry } from "../../../shared/api/followRun";
import { invalidateMockupPointer } from "../../../shared/api/invalidateMockupPointer";
import {
  conversationMessagesKey,
  conversationStateKey,
} from "../../../shared/api/queryKeys";
import type {
  ConversationStateView,
  GenerationDetail,
  TurnAccepted,
  TurnMode,
  TurnStatusName,
  TurnView,
} from "../../../types/api";
import { isActivePhase } from "../../../types/generation";
import type { GenerationPhase, RunProgress } from "../../../types/generation";
import {
  cancelTurn,
  confirmBrief,
  editMessage,
  fetchConversationState,
  retryTurn,
  startTurn,
  streamTurnRun,
} from "../api/conversationApi";

export { DEFAULT_STREAM_RETRY };
export type { StreamRetry };

export type CancelOutcome = "raised" | "refused";

export interface TurnState {
  readonly phase: GenerationPhase;
  /** The refusal of the POST (busy, capacity, invalid), for the composer. */
  readonly error: ApiError | null;
  /** The run being followed, once the server has named it. */
  readonly generationId: string | null;
}

/** The reference image of a send (F-CNV-012): its plain base64 and the digest of its bytes. */
export interface AttachedImage {
  readonly base64: string;
  readonly digest: string;
}

export interface UseTurnResult extends TurnState {
  /** Where the followed run is in its flow, as its stream last said (F-PRV-003); `null` before. */
  readonly progress: RunProgress | null;
  /** `true` when the server accepted the turn, so the composer may clear its text. */
  readonly start: (
    text: string,
    mode?: TurnMode,
    reviewBrief?: boolean,
    image?: AttachedImage,
  ) => Promise<boolean>;
  /**
   * Run the last closed turn again. `turnId` is the turn being retried: it only names the
   * intention, so a second press after a lost answer reuses the reserved id of the first.
   */
  readonly retryLast: (turnId: string) => Promise<boolean>;
  /** Draw from the brief of the `briefed` turn `briefedTurnId`, as the person left it. */
  readonly confirm: (briefedTurnId: string, brief: string) => Promise<boolean>;
  /** Say the earlier message `messageId` differently, from where it was said. */
  readonly edit: (messageId: string, text: string) => Promise<boolean>;
  /**
   * `raised`: the signal went up for THIS run (or it had already ended). The body names the run's
   * turn and the server cancels only that one (9.6), so a late cancel never stops a newer turn.
   * `refused`: nothing was stopped.
   */
  readonly cancel: () => Promise<CancelOutcome>;
  /** Follow a turn that is already open and was not started here. */
  readonly adopt: (turnId: string, generationId: string, status?: TurnStatusName) => void;
  /** Forget a refusal. */
  readonly dismissError: () => void;
}

/** How a closed turn ended, as the person sees it, by the status the server reports. */
const OUTCOME_BY_STATUS: Partial<Record<TurnStatusName, GenerationPhase>> = {
  asked: "asked",
  briefed: "briefed",
  succeeded: "succeeded",
  failed: "failed",
  cancelled: "cancelled",
  interrupted: "interrupted",
};

/** The run's own turn, while the server shows it open. */
function ownOpenTurn(run: Run, read: ConversationStateView): TurnView | null {
  return read.turn?.id === run.turnId ? read.turn : null;
}

/**
 * The run's own turn once the server shows it CLOSED: it is `last_turn` AND carries this id.
 * A snapshot that shows neither (read before the POST landed, or a turn that is somebody
 * else's) has not closed it, and claims nothing.
 */
function ownClosedTurn(run: Run, read: ConversationStateView): TurnView | null {
  return read.last_turn?.id === run.turnId ? read.last_turn : null;
}

/**
 * The key of a plain send: the same text resent in the same mode keeps its id; the same text
 * in another mode is another intention and gets another one. Modifying is the default and
 * keeps the plain key (the first message of a new mockup is reserved under it).
 */
function textKey(
  text: string,
  mode?: TurnMode,
  reviewBrief = false,
  image?: AttachedImage,
): string {
  const base = mode === "regenerate" ? `regenerate:${text}` : `text:${text}`;
  // Asking to review the brief is another intention too (F-CNV-016).
  const briefed = reviewBrief ? `review:${base}` : base;
  // Another image is another intention too (F-CNV-012): the digest names its content, not its file.
  return image === undefined ? briefed : `image:${image.digest}:${briefed}`;
}

/** Forget the reservations of the turn `turnId`: the server answered for it. */
function releaseTurn(reservations: Map<string, PendingSend>, turnId: string): void {
  for (const [key, held] of reservations) {
    if (held.id === turnId) reservations.delete(key);
  }
}

const IDLE: TurnState = { phase: "idle", error: null, generationId: null };

/** `shown`, or the progress `event` reports when it is not behind what is shown of its run. */
export function advance(shown: RunProgress | null, event: GenerationDetail): RunProgress | null {
  const next = progressOf(event);
  // An event without stages keeps what is shown of ITS run only: another run's stages never show.
  if (next === null) return shown?.generationId === event.generation_id ? shown : null;
  const behind = shown?.generationId === next.generationId && next.stepsDone < shown.stepsDone;
  return behind ? shown : next;
}

/** The staged progress an event of the stream reports, or `null` from a server without it. */
function progressOf(event: GenerationDetail): RunProgress | null {
  const { stage, steps_done: stepsDone, steps_total: stepsTotal, step_roles: stepRoles } = event;
  if (stage === undefined || stepsDone === undefined || stepsTotal === undefined || stepRoles === undefined) {
    return null;
  }
  return { generationId: event.generation_id, stage, stepsDone, stepsTotal, stepRoles };
}

interface Run {
  readonly turnId: string;
  readonly generationId: string;
  readonly controller: AbortController;
  cancelRequested: boolean;
  cancelInFlight: boolean;
  finished: boolean;
  /** The status the server last showed for this turn (`null`: not visible yet), for a later re-follow. */
  lastStatus: TurnStatusName | null;
}

/** A send the server has not answered for yet: its text and the id it travels under. */
interface PendingSend {
  /** The intention (a text, a retry of a turn, an edit of a message): same key, same id. */
  readonly key: string;
  readonly id: string;
  /** The answer may have been lost: the server could already hold this turn. */
  ambiguous: boolean;
}

export function useTurn(
  mockupId: string,
  retry: StreamRetry = DEFAULT_STREAM_RETRY,
): UseTurnResult {
  const queryClient = useQueryClient();
  const [state, setState] = useState<TurnState>(IDLE);
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const current = useRef<Run | null>(null);
  const phase = useRef<GenerationPhase>("idle");
  /** Every send the server has not answered for yet, by intention: each keeps its own id. */
  const pending = useRef(new Map<string, PendingSend>());
  const startAbort = useRef<AbortController | null>(null);
  /** Bumped whenever something supersedes what an `await` was about to do (a send, a follow, unmount). */
  const epoch = useRef(0);

  const publish = useCallback((run: Run | null, next: TurnState): void => {
    if (current.current !== run) return;
    phase.current = next.phase;
    setState(next);
  }, []);

  /** The conversation state, fresh, or `"unknown"` when it cannot be read. */
  const readState = useCallback(async (): Promise<ConversationStateView | "unknown"> => {
    try {
      return await queryClient.query({
        queryKey: conversationStateKey(mockupId),
        queryFn: () => fetchConversationState(mockupId),
        staleTime: 0,
      });
    } catch {
      return "unknown";
    }
  }, [queryClient, mockupId]);

  /**
   * Read the state again, with a growing and bounded pause, until `done` holds for it
   * (`done` is pure; `onRead` sees every read). `null` when the budget ends first or the
   * run is abandoned: nothing was learned.
   */
  const awaitState = useCallback(
    async (
      run: Run,
      done: (read: ConversationStateView) => boolean,
      onRead?: (read: ConversationStateView) => void,
    ): Promise<ConversationStateView | null> => {
      const { signal } = run.controller;
      // A function: the abort happens while the awaits below are pending.
      const stopped = (): boolean => signal.aborted;
      for (let attempt = 0; attempt < retry.attempts && !stopped(); attempt += 1) {
        const read = await readState();
        if (stopped()) return null;
        if (read !== "unknown") {
          run.lastStatus = ownOpenTurn(run, read)?.status ?? null;
          onRead?.(read);
          if (done(read)) return read;
        }
        await wait(backoffDelay(retry, attempt), signal);
      }
      return null;
    },
    [readState, retry],
  );

  const finish = useCallback(
    (run: Run, next: TurnState): void => {
      if (run.finished) return;
      run.finished = true;
      // Whether or not this screen is still the one showing the turn: the thread (its
      // questions, update or error), the tree and the pointer of THIS mockup are stale.
      void invalidateMockupPointer(queryClient, mockupId);
      publish(run, next);
    },
    [publish, queryClient, mockupId],
  );

  const follow = useCallback(
    async (run: Run): Promise<void> => {
      const { signal } = run.controller;
      const live = (base: GenerationPhase): GenerationPhase =>
        run.cancelRequested ? "cancelling" : base;
      // Publishes only a CHANGE of phase: a poll that sees the same thing sets no state.
      const show = (phaseName: GenerationPhase): void => {
        if (phase.current === phaseName) return;
        publish(run, { phase: phaseName, error: null, generationId: run.generationId });
      };
      const interrupted = (): void => {
        if (!signal.aborted) {
          finish(run, { phase: "interrupted", error: null, generationId: run.generationId });
        }
      };

      // 1. Evaluating: the turn is `assessing`, or not visible yet (a read that was in flight
      //    before the POST landed). Either way there is no run to stream. Read the state until
      //    the turn is dispatched, cancelling, or closed; the budget bounds the wait.
      let read = await awaitState(
        run,
        (seen) => {
          const own = ownOpenTurn(run, seen);
          return own === null ? ownClosedTurn(run, seen) !== null : own.status !== "assessing";
        },
        (seen) => {
          const own = ownOpenTurn(run, seen);
          if (own === null ? ownClosedTurn(run, seen) === null : own.status === "assessing") {
            show(live("evaluating"));
          }
        },
      );
      if (read === null) {
        interrupted();
        return;
      }

      // 2. Only a DISPATCHED turn has a run record: its stream is followed for the progress.
      //    A cancelling turn may never have been dispatched (404 for good): do not ask for it.
      const own = ownOpenTurn(run, read);
      if (own?.status === "cancel_requested") show("cancelling");
      if (own?.status === "dispatched") {
        let ended = false;
        await followStream({
          open: () => streamTurnRun(mockupId, run.generationId, signal),
          onEvent: (event: GenerationDetail): void => {
            // The record's end decides nothing: the turn closes AFTER it (summary first).
            if (event.status === "in_progress") {
              // Stages of another run say nothing about this one (checklist 14).
              // No reset between runs: a run's phase is `running` only from its own first event.
              // A stage never moves backwards: an older reading of the same run is dropped.
              if (event.generation_id === run.generationId) setProgress((shown) => advance(shown, event));
              show(live("running"));
            } else ended = true;
          },
          isDone: () => ended,
          signal,
          retry,
          reopenOnClose: true,
        });
        if (signal.aborted) return;
      }

      // 3. The outcome is the closed turn's: wait for the server to close it.
      if (own !== null) {
        read = await awaitState(run, (seen) => ownClosedTurn(run, seen) !== null);
        if (read === null) {
          interrupted();
          return;
        }
      }
      const closed = ownClosedTurn(run, read);
      if (closed === null) {
        interrupted();
        return;
      }
      finish(run, {
        phase: OUTCOME_BY_STATUS[closed.status] ?? "idle",
        error: null,
        generationId: run.generationId,
      });
    },
    [mockupId, retry, publish, finish, awaitState],
  );

  const open = useCallback(
    (turnId: string, generationId: string): void => {
      epoch.current += 1;
      current.current?.controller.abort();
      const run: Run = {
        turnId,
        generationId,
        controller: new AbortController(),
        cancelRequested: false,
        cancelInFlight: false,
        finished: false,
        lastStatus: null,
      };
      current.current = run;
      publish(run, { phase: "starting", error: null, generationId });
      void follow(run);
    },
    [publish, follow],
  );

  /**
   * A turn that closed before this screen could follow it (a lost answer, a reload): show its
   * outcome as the state reports it, with nothing to stream.
   */
  const showClosed = useCallback(
    (last: TurnView): void => {
      current.current?.controller.abort();
      current.current = null;
      releaseTurn(pending.current, last.id);
      const phaseOfLast = OUTCOME_BY_STATUS[last.status] ?? "idle";
      phase.current = phaseOfLast;
      setState({ phase: phaseOfLast, error: null, generationId: last.generation_id });
      void invalidateMockupPointer(queryClient, mockupId);
    },
    [queryClient, mockupId],
  );

  /**
   * The state may already hold the turn with this id: follow it if it is open, show its outcome
   * if it closed. `"none"` when the state does not show it either way; `"abandoned"` when what
   * was being done has been superseded meanwhile, so nothing is touched.
   */
  const adoptOwn = useCallback(
    (
      read: ConversationStateView,
      turnId: string,
      abandoned: () => boolean,
    ): "followed" | "closed" | "abandoned" | "none" => {
      if (abandoned()) return "abandoned";
      const { turn, last_turn: last } = read;
      if (turn?.id === turnId && turn.generation_id !== null) {
        releaseTurn(pending.current, turnId);
        void queryClient.invalidateQueries({ queryKey: conversationMessagesKey(mockupId) });
        open(turn.id, turn.generation_id);
        return "followed";
      }
      if (last?.id === turnId) {
        showClosed(last);
        return "closed";
      }
      return "none";
    },
    [queryClient, mockupId, open, showClosed],
  );

  const send = useCallback(
    async (
      key: string,
      call: (turnId: string, signal: AbortSignal) => Promise<TurnAccepted>,
    ): Promise<boolean> => {
      // The same intention repeated keeps its id; any other is a new send.
      const attempt: PendingSend =
        pending.current.get(key) ?? { key, id: crypto.randomUUID(), ambiguous: false };
      pending.current.set(key, attempt);
      epoch.current += 1;
      startAbort.current?.abort();
      const controller = new AbortController();
      // A function: the abort happens while the awaits below are pending.
      const abandoned = (): boolean => controller.signal.aborted;
      startAbort.current = controller;
      setState({ ...IDLE, phase: "starting" });
      phase.current = "starting";
      current.current?.controller.abort();
      current.current = null;
      try {
        const accepted = await call(attempt.id, controller.signal);
        // Abandoned while the server was answering: nothing is opened or polled.
        if (abandoned()) return false;
        pending.current.delete(key);
        // The person's own message is already in the thread: read it.
        void queryClient.invalidateQueries({ queryKey: conversationMessagesKey(mockupId) });
        open(accepted.turn_id, accepted.generation_id);
        return true;
      } catch (cause) {
        if (abandoned()) return false;
        const error = asApiError(cause);
        let settled = true;
        if (error.status === 0 || error.status >= 500) {
          // No answer to speak of: the turn may exist, so the id stays with the text.
          pending.current.set(key, { ...attempt, ambiguous: true });
          settled = false;
        } else if (error.status === 409) {
          // Busy, or the id is already taken: either way the server holds a turn this
          // screen may not know. Find it, and follow it if it is the one sent here.
          void queryClient.invalidateQueries({ queryKey: conversationStateKey(mockupId) });
          if (attempt.ambiguous) {
            const read = await readState();
            if (abandoned()) return false;
            if (read !== "unknown") {
              const adopted = adoptOwn(read, attempt.id, abandoned);
              if (adopted === "abandoned") return false;
              if (adopted !== "none") return true;
              if (read.turn === null) {
                // Nothing is open, yet the server says the id is taken: an earlier turn
                // holds it. The attempt is over, not failed: the thread holds its result,
                // and the id is let go.
                pending.current.delete(key);
                phase.current = "idle";
                setState(IDLE);
                void invalidateMockupPointer(queryClient, mockupId);
                return true;
              }
            }
            settled = false;
          }
        }
        if (settled) pending.current.delete(key);
        phase.current = "idle";
        setState({ ...IDLE, error });
        return false;
      }
    },
    [mockupId, queryClient, open, readState, adoptOwn],
  );

  const start = useCallback(
    (text: string, mode?: TurnMode, reviewBrief = false, image?: AttachedImage): Promise<boolean> =>
      send(textKey(text, mode, reviewBrief, image), (turnId, signal) =>
        startTurn(mockupId, { text, mode, reviewBrief, imageBase64: image?.base64, turnId, signal }),
      ),
    [mockupId, send],
  );

  const retryLast = useCallback(
    (turnId: string): Promise<boolean> =>
      send(`retry:${turnId}`, (reserved, signal) =>
        retryTurn(mockupId, { turnId: reserved, signal }),
      ),
    [mockupId, send],
  );

  const confirm = useCallback(
    (briefedTurnId: string, brief: string): Promise<boolean> =>
      send(`brief:${briefedTurnId}:${brief}`, (reserved, signal) =>
        confirmBrief(mockupId, briefedTurnId, { brief, turnId: reserved, signal }),
      ),
    [mockupId, send],
  );

  const edit = useCallback(
    (messageId: string, text: string): Promise<boolean> =>
      send(`edit:${messageId}:${text}`, (reserved, signal) =>
        editMessage(mockupId, messageId, { text, turnId: reserved, signal }),
      ),
    [mockupId, send],
  );

  const adopt = useCallback(
    (turnId: string, generationId: string, status?: TurnStatusName): void => {
      if (isActivePhase(phase.current)) return;
      const run = current.current;
      if (run?.turnId === turnId) {
        // The turn this screen already followed to its end: the state may still say so. Only a
        // turn this screen GAVE UP on is followed again, and only when the server now shows it
        // somewhere else than where it was left (later dispatched, say): never a loop on the same.
        if (phase.current !== "interrupted" || status === undefined || status === run.lastStatus) {
          return;
        }
      }
      open(turnId, generationId);
    },
    [open],
  );

  const cancel = useCallback(async (): Promise<CancelOutcome> => {
    const run = current.current;
    if (
      run === null ||
      (phase.current !== "starting" && phase.current !== "evaluating" && phase.current !== "running")
    ) {
      return "refused";
    }
    if (run.cancelInFlight) return "raised";
    run.cancelInFlight = true;
    const { signal } = run.controller;
    const abandoned = (): boolean => signal.aborted;
    try {
      // A `202` is about this turn: the server stops only the one the body names (9.6).
      await cancelTurn(mockupId, run.turnId, signal);
      run.cancelRequested = true;
      if (!run.finished) {
        publish(run, { phase: "cancelling", error: null, generationId: run.generationId });
      }
      return "raised";
    } catch (cause) {
      // Abandoned: nobody is left to tell. `409`: the run had already ended, and its own
      // stream says how, so there is nothing to refuse.
      return abandoned() || (cause instanceof ApiError && cause.status === 409)
        ? "raised"
        : "refused";
    } finally {
      run.cancelInFlight = false;
    }
  }, [mockupId, publish]);

  const dismissError = useCallback((): void => {
    if (phase.current === "idle") setState(IDLE);
  }, []);

  useEffect(
    () => () => {
      epoch.current += 1;
      startAbort.current?.abort();
      current.current?.controller.abort();
      current.current = null;
      // What was in flight is abandoned, so nothing is active any more: a mount that
      // follows at once (React runs every effect twice in development) may open it again.
      phase.current = "idle";
    },
    [],
  );

  return { ...state, progress, start, retryLast, confirm, edit, cancel, adopt, dismissError };
}
